import { useEffect, useMemo, useState } from "react";
import { ExternalLink, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import {
  EmptyState,
  PageHeader,
  Panel,
  StatusBadge,
  shortDate,
} from "@/components/admin/uiKit";

const SOURCE_FILTERS = [
  { id: "all", label: "All" },
  { id: "out_of_area", label: "Out of Area" },
  { id: "plans_custom", label: "Custom Plan" },
  { id: "admin", label: "Admin" },
  { id: "other", label: "Other" },
];

const STATUS_OPTIONS = ["new", "contacted", "quoted", "closed"];

export default function AdminQuoteInbox() {
  const [items, setItems] = useState(null);
  const [source, setSource] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => {
    adminApi
      .get("/admin/custom-quote-requests")
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatAdminError(err)));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    if (source === "all") return items;
    return items.filter((i) => (i.source || "out_of_area") === source);
  }, [items, source]);

  const counts = useMemo(() => {
    if (!items) return {};
    return items.reduce((acc, i) => {
      const s = i.source || "out_of_area";
      acc[s] = (acc[s] || 0) + 1;
      acc.all = (acc.all || 0) + 1;
      return acc;
    }, {});
  }, [items]);

  return (
    <div data-testid="admin-quote-inbox">
      <PageHeader
        overline="Operations"
        title="Quote Inbox"
        description="Out-of-area waitlists and custom plan requests. Move them through new → contacted → quoted → closed."
      />

      <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-quote-inbox-filters">
        {SOURCE_FILTERS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSource(s.id)}
            className={`text-xs font-bold uppercase tracking-[0.18em] px-3 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
              source === s.id
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`admin-quote-source-${s.id}`}
          >
            {s.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${source === s.id ? "bg-white/15" : "bg-slate-100"}`}>
              {counts[s.id] || 0}
            </span>
          </button>
        ))}
      </div>

      {filtered === null ? (
        <Panel className="p-10 text-center">
          <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
        </Panel>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No requests in this bucket"
          description="When new quote requests come in, they will land here."
        />
      ) : (
        <Panel className="overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {filtered.map((q) => (
              <li
                key={q.id}
                className="px-5 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => setSelected(q)}
                data-testid={`admin-quote-row-${q.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <p className="font-bold text-slate-900 truncate max-w-full">{q.full_name}</p>
                      <StatusBadge status={q.status} />
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                        {(q.source || "out_of_area").replace(/_/g, " ")}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 break-words">
                      {q.email} · {q.phone} · {q.zip}
                    </p>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                      {q.message}
                    </p>
                  </div>
                  <div className="text-xs text-slate-500 shrink-0 text-right">
                    {shortDate(q.created_at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {selected && (
        <QuoteDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => {
            setItems((prev) =>
              (prev || []).map((p) => (p.id === updated.id ? updated : p)),
            );
            setSelected(updated);
          }}
        />
      )}
    </div>
  );
}

function QuoteDrawer({ item, onClose, onUpdated }) {
  const [status, setStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await adminApi.patch(
        `/admin/custom-quote-requests/${item.id}`,
        { status: next },
      );
      setStatus(next);
      onUpdated?.(data.item);
      toast.success(`Status set to ${next}`);
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      data-testid="admin-quote-drawer"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
            Custom Quote Request
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center"
          >
            <X size={16} />
          </button>
        </header>

        <div className="p-6 space-y-5">
          <div>
            <h2
              className="text-2xl font-extrabold tracking-tight text-slate-900"
              style={{ fontFamily: "Outfit, ui-sans-serif" }}
            >
              {item.full_name}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {item.email} · {item.phone}
            </p>
            <p className="text-sm text-slate-500 inline-flex items-center gap-1.5 mt-1">
              <MapPin size={12} /> {item.service_address}, {item.zip}
            </p>
          </div>

          <Panel className="p-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
              Source
            </p>
            <p className="text-sm text-slate-700 font-semibold mb-3">
              {(item.source || "out_of_area").replace(/_/g, " ")}
            </p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
              Bin count
            </p>
            <p className="text-sm text-slate-700">{item.bin_count}</p>
          </Panel>

          <Panel className="p-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
              Message
            </p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {item.message}
            </p>
          </Panel>

          <Panel className="p-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
              Status
            </p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => save(s)}
                  disabled={saving}
                  className={`text-xs font-bold uppercase tracking-[0.18em] px-3 py-2 rounded-lg border transition-colors ${
                    status === s
                      ? "bg-sky-600 text-white border-sky-600"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`admin-quote-status-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Panel>

          <div className="flex flex-wrap gap-2">
            <a
              href={`mailto:${item.email}?subject=Re%3A%20your%20Silky%20Smooth%20quote%20request`}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 h-10 transition-colors"
              data-testid="admin-quote-email-link"
            >
              <ExternalLink size={14} /> Email customer
            </a>
            <a
              href={`tel:${item.phone}`}
              className="inline-flex items-center gap-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-sm font-semibold px-4 h-10 transition-colors"
              data-testid="admin-quote-phone-link"
            >
              Call
            </a>
          </div>
        </div>
      </aside>
    </div>
  );
}
